/**
 * Days, weeks, months and typed times on the practice's clock.
 *
 * Every time on screen was already formatted in the practice's timezone
 * (i18n/index.js), but days and weeks were cut, and typed times were read, on
 * the browser's clock. In Buenos Aires the two agree. For a patient abroad
 * booking an online session, or a professional travelling, they do not: a 21:00
 * slot sat in the next day's column, and a typed "10:15" booked a different
 * instant from the one the agenda then displayed. The database lays the grid out
 * in the practice's zone (available_slots); this cuts it the same way on screen.
 *
 * Days travel as YYYY-MM-DD keys. formatDate reads a key as a calendar date, so
 * a key can be handed straight to it for a label.
 */

import { practiceTimezone } from '../i18n';

const pad = (n) => String(n).padStart(2, '0');

/** What the practice's wall clock reads at an instant, as numbers. */
const wallClock = (instant) => Object.fromEntries(
  new Intl.DateTimeFormat('en-US', {
    timeZone: practiceTimezone(), hourCycle: 'h23',
    year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: 'numeric', second: 'numeric',
  }).formatToParts(new Date(instant))
    .filter((part) => part.type !== 'literal')
    .map((part) => [part.type, Number(part.value)]),
);

/** The practice's calendar date at an instant (now by default), as YYYY-MM-DD. */
export const dayKey = (instant = Date.now()) => {
  const w = wallClock(instant);
  return `${w.year}-${pad(w.month)}-${pad(w.day)}`;
};

/** A day key moved by whole calendar days. */
export const addDaysToKey = (key, days) => {
  const [year, month, day] = key.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
};

/** The instant the practice's clock reads `time` (HH:MM) on day `key`. */
export const instantAt = (key, time = '00:00') => {
  const [year, month, day] = key.split('-').map(Number);
  const [hours, minutes] = time.split(':').map(Number);
  const target = Date.UTC(year, month - 1, day, hours, minutes);
  let guess = target;
  // Twice, so a daylight-saving change between the first guess and the answer
  // is read from the right side of it.
  for (let i = 0; i < 2; i += 1) {
    const w = wallClock(guess);
    guess = target - (Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second) - guess);
  }
  return new Date(guess);
};

/** A practice day as a half-open range of ISO instants. */
export const dayRange = (key) => ({
  start: instantAt(key).toISOString(),
  end: instantAt(addDaysToKey(key, 1)).toISOString(),
});

/** Monday of the week containing day `key`. */
export const weekStartKey = (key) => {
  const [year, month, day] = key.split('-').map(Number);
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay(); // 0 = Sunday
  return addDaysToKey(key, -((weekday + 6) % 7));
};

/** A datetime-local input value showing `instant` on the practice's clock. */
export const toInputValue = (instant) => {
  const w = wallClock(instant);
  return `${w.year}-${pad(w.month)}-${pad(w.day)}T${pad(w.hour)}:${pad(w.minute)}`;
};

/**
 * A datetime-local input value read as the practice's wall clock. The browser
 * would read it on its own clock. Returns null for an incomplete value.
 */
export const fromInputValue = (value) => {
  if (!value || !value.includes('T')) return null;
  const [key, time] = value.split('T');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(key) || !/^\d{2}:\d{2}/.test(time)) return null;
  const instant = instantAt(key, time.slice(0, 5));
  return Number.isNaN(instant.getTime()) ? null : instant;
};

/** The practice's month at an instant (now by default), as YYYY-MM. */
export const monthKey = (instant = Date.now()) => dayKey(instant).slice(0, 7);

/** A YYYY-MM month moved by whole months. */
export const addMonthsToKey = (key, months) => {
  const [year, month] = key.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1 + months, 1)).toISOString().slice(0, 7);
};

/** A practice month as a half-open range of ISO instants. */
export const monthRange = (key) => ({
  start: instantAt(`${key}-01`).toISOString(),
  end: instantAt(`${addMonthsToKey(key, 1)}-01`).toISOString(),
});

/**
 * Whether a calendar date (YYYY-MM-DD) is already behind the practice's today
 * at an instant (now by default). A due date is a day, so it is overdue from
 * the next day on; new Date("2026-09-30") < new Date() said so from 21:00 on
 * the 29th in Buenos Aires.
 */
export const dayHasPassed = (key, instant = Date.now()) => key < dayKey(instant);
